import type { TimelineMarkerEnum } from "../types";

interface IPos {
  x: number;
  y: number;
}

interface IPathGeneratorCtx {
  boxRect: DOMRect;
  markerRect: DOMRect;
  itemRect: DOMRect;
  nextItemEl?: Element | null;
  itemIdx: number;
  timelineMarker: TimelineMarkerEnum;
  pathWidth: number;
  applyScaleToRect: (rect: DOMRect) => void;
  scale: IPos;
  drawCurvePoint: (x: number, y: number, fill?: string) => void;
}

interface IPathGeneratorResult {
  path: string;
}

export type IPathGenerator = (ctx: IPathGeneratorCtx) => IPathGeneratorResult;

export const makeTimelinePathStyleLine: IPathGenerator = ({
  boxRect,
  markerRect,
  itemRect,
  pathWidth,
}) => {
  let path = "";
  path += `M${boxRect.width / 2},${markerRect.height + pathWidth / 2 + 16} `;
  path += `L${boxRect.width / 2},${itemRect.height - pathWidth / 2}`;
  return { path };
};

// Quarter-wave per item — assumes markers alternate between left/right
// edges of the track box (i.e. `timelineContentPlacement: "alternating"`).
// Forcing endpoints to a measured marker centre collapses the wave to
// an S-curve when all markers sit on the same side.
export const makeTimelinePathStylePath: IPathGenerator = ({
  nextItemEl,
  boxRect,
  itemIdx,
  markerRect,
  applyScaleToRect,
  pathWidth,
  drawCurvePoint,
}) => {
  if (!nextItemEl) return { path: "" };

  let path = "";
  const isFromLeft = itemIdx % 2 === 0;

  const nextMarkerEl = nextItemEl.querySelector(".timeline-item-marker");
  if (!nextMarkerEl) return { path: "" };

  const nextMarkerRect = nextMarkerEl.getBoundingClientRect();

  applyScaleToRect(nextMarkerRect);
  const pathHeight = boxRect.height - (markerRect.height + 16);

  let startPos: IPos = {
    x: markerRect.width * 0.5 + pathWidth / 2,
    y: markerRect.height + 16,
  };
  let endPos: IPos = {
    x: boxRect.width - nextMarkerRect.width * 0.5,
    y: boxRect.height - pathWidth,
  };

  let arm1Pos: IPos = {
    x: startPos.x,
    y: startPos.y + pathHeight * 0.4,
  };
  let arm2Pos: IPos = {
    x: endPos.x,
    y: endPos.y - pathHeight * 0.4,
  };

  if (isFromLeft) {
    if (endPos.x - startPos.x < 40) {
      arm1Pos.x = startPos.x + boxRect.width * 0.2;
      arm1Pos.y = startPos.y + pathHeight * 0.2;

      endPos.x = boxRect.width - 30;

      arm2Pos.x = endPos.x;
      arm2Pos.y = endPos.y - pathHeight * 0.2;
    }
  } else {
    startPos = {
      x: boxRect.width - markerRect.width * 0.5 + pathWidth / 2,
      y: markerRect.height + 16,
    };
    endPos = {
      x: nextMarkerRect.width * 0.5,
      y: boxRect.height - pathWidth,
    };

    arm1Pos = {
      x: startPos.x,
      y: startPos.y + pathHeight * 0.4,
    };
    arm2Pos = {
      x: endPos.x,
      y: endPos.y - pathHeight * 0.4,
    };

    if (Math.abs(endPos.x - startPos.x) < 40) {
      arm1Pos.x = startPos.x - boxRect.width * 0.2;
      arm1Pos.y = startPos.y + pathHeight * 0.2;

      endPos.x = 30;

      arm2Pos.x = endPos.x;
      arm2Pos.y = endPos.y - pathHeight * 0.2;
    }
  }

  path += `M${startPos.x},${startPos.y}`;
  path += " ";
  path += `C${arm1Pos.x},${arm1Pos.y}, ${arm2Pos.x},${arm2Pos.y} ${endPos.x},${endPos.y}`;

  drawCurvePoint(arm1Pos.x, arm1Pos.y);
  drawCurvePoint(arm2Pos.x, arm2Pos.y, "purple");

  return { path };
};
